import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  ReactNode,
} from 'react';
import { chatApi, ApiError } from '../lib/apiClient';
import type { ChatSession, ChatMessage } from '../types';
import { useAuth } from './AuthContext';

/** A structured, user-displayable error with optional diagnostics. */
export interface ChatError {
  /** Human-readable message shown to the user. */
  message: string;
  /** Stable error code (e.g. AGENT_BUSY, NETWORK_ERROR) for branching/support. */
  code?: string;
  /** API Gateway request id, when available, to correlate with server logs. */
  requestId?: string;
}

interface ChatState {
  sessions: ChatSession[];
  activeSession: ChatSession | null;
  messages: ChatMessage[];
  isSendingMessage: boolean;
  isLoadingSessions: boolean;
  isLoadingMessages: boolean;
  error: ChatError | null;
  selectSession: (sessionId: string) => Promise<void>;
  sendMessage: (message: string) => Promise<void>;
  deleteSession: (sessionId: string) => Promise<void>;
  newSession: () => void;
  clearError: () => void;
}

/** Normalize any thrown value into a ChatError and log full detail to console. */
function toChatError(err: unknown, context: string): ChatError {
  if (err instanceof ApiError) {
    console.error(
      `[chat] ${context} failed:`,
      { code: err.code, status: err.status, requestId: err.requestId, details: err.details },
      err,
    );
    return { message: err.message, code: err.code, requestId: err.requestId };
  }
  const message = err instanceof Error ? err.message : String(err);
  console.error(`[chat] ${context} failed:`, err);
  return { message };
}

const ChatContext = createContext<ChatState | null>(null);

export function ChatProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();

  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [activeSession, setActiveSession] = useState<ChatSession | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [error, setError] = useState<ChatError | null>(null);

  // Load sessions on auth
  const loadSessions = useCallback(async () => {
    setIsLoadingSessions(true);
    try {
      const data = await chatApi.listSessions();
      setSessions(data);
    } catch (err) {
      setError(toChatError(err, 'Loading chat history'));
    } finally {
      setIsLoadingSessions(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) loadSessions();
    else setSessions([]);
  }, [isAuthenticated, loadSessions]);

  const selectSession = useCallback(async (sessionId: string) => {
    setIsLoadingMessages(true);
    setError(null);
    try {
      const [session, msgs] = await Promise.all([
        chatApi.getSession(sessionId),
        chatApi.getMessages(sessionId),
      ]);
      setActiveSession(session);
      setMessages(msgs);
    } catch (err) {
      setError(toChatError(err, 'Loading conversation'));
    } finally {
      setIsLoadingMessages(false);
    }
  }, []);

  const sendMessage = useCallback(
    async (message: string) => {
      if (!message.trim()) return;
      setIsSendingMessage(true);
      setError(null);

      // Optimistically add user message to the UI
      const optimisticUserMsg: ChatMessage = {
        messageId: `optimistic-${Date.now()}`,
        sessionId: activeSession?.sessionId ?? '',
        userId: '',
        role: 'user',
        content: message.trim(),
        createdAt: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, optimisticUserMsg]);

      try {
        const response = await chatApi.sendMessage({
          sessionId: activeSession?.sessionId,
          message: message.trim(),
        });

        const assistantMsg: ChatMessage = {
          messageId: response.messageId,
          sessionId: response.sessionId,
          userId: 'assistant',
          role: 'assistant',
          content: response.content,
          createdAt: response.createdAt,
          metadata: response.metadata,
        };

        // Patch the optimistic message with correct sessionId if new session was created
        setMessages((prev) =>
          prev.map((m) =>
            m.messageId === optimisticUserMsg.messageId
              ? { ...m, sessionId: response.sessionId }
              : m,
          ).concat(assistantMsg),
        );

        // If this was a new session, add it to the list and select it
        if (!activeSession) {
          const newSession = await chatApi.getSession(response.sessionId);
          setActiveSession(newSession);
          setSessions((prev) => [newSession, ...prev]);
        } else {
          // Update the session's updatedAt and message count
          setSessions((prev) =>
            prev.map((s) =>
              s.sessionId === response.sessionId
                ? { ...s, messageCount: s.messageCount + 2, updatedAt: response.createdAt }
                : s,
            ),
          );
        }
      } catch (err) {
        // Remove optimistic message on failure
        setMessages((prev) =>
          prev.filter((m) => m.messageId !== optimisticUserMsg.messageId),
        );
        setError(toChatError(err, 'Sending message'));
      } finally {
        setIsSendingMessage(false);
      }
    },
    [activeSession],
  );

  const deleteSession = useCallback(
    async (sessionId: string) => {
      try {
        await chatApi.deleteSession(sessionId);
        setSessions((prev) => prev.filter((s) => s.sessionId !== sessionId));
        if (activeSession?.sessionId === sessionId) {
          setActiveSession(null);
          setMessages([]);
        }
      } catch (err) {
        setError(toChatError(err, 'Deleting conversation'));
      }
    },
    [activeSession],
  );

  const newSession = useCallback(() => {
    setActiveSession(null);
    setMessages([]);
    setError(null);
  }, []);

  const clearError = useCallback(() => setError(null), []);

  return (
    <ChatContext.Provider
      value={{
        sessions,
        activeSession,
        messages,
        isSendingMessage,
        isLoadingSessions,
        isLoadingMessages,
        error,
        selectSession,
        sendMessage,
        deleteSession,
        newSession,
        clearError,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
}

export function useChat(): ChatState {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error('useChat must be used inside <ChatProvider>');
  return ctx;
}
